import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from 'sweetalert2';
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { useSidebar } from "../../SidebarContext";
import Header from "../Header";
import Sidebar from "../SideBar";
import { User, Info, MapPin, Phone, StickyNote, ArrowLeftIcon, PenIcon, Trash2Icon, FileText } from "lucide-react";
import { decryptToken } from "../utils/getToken";


const CustomerDetail = () => {
    const { isSidebarOpen } = useSidebar();
    const { id } = useParams();
    const [customer, setCustomer] = useState(null);
    const navigate = useNavigate()
    const url = process.env.REACT_APP_LINK;
    const user = getDecryptedUserFromCookie("gta_mainstateuser");
    const token = decryptToken("accessToken") || ""

    useEffect(() => {
        const fetchCustomer = async () => {
            try {
                const res = await fetch(`${url}/api/customer/${id}`, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                });
                const result = await res.json();
                const data = result.customer;
                const parsedCustomer = {
                    ...data,
                    billingAddress: data.billingAddress ? JSON.parse(data.billingAddress) : {},
                    shippingAddress: data.shippingAddress ? JSON.parse(data.shippingAddress) : {},
                    contact: data.contact ? JSON.parse(data.contact) : [],
                };
                console.log("Single Detail", data)
                setCustomer(parsedCustomer);
            } catch (error) {
                console.error("Error loading customer:", error);
            }
        };

        if (id) fetchCustomer();
    }, [id]);

    if (!customer) return <p>Loading customer details...</p>;

    const { billingAddress, shippingAddress, contact } = customer;
    const firstContact = contact?.[0] || {};

    const handleEdit = (customer) => {
        navigate('/edit-customer', { state: customer });

    };

    const handleDelete = async (customerId) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "This customer will be permanently deleted!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await fetch(`${url}/api/customer/${customerId}`, {
                        method: 'DELETE',
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    });

                    if (!response.ok) {
                        const err = await response.json();
                        throw new Error(err?.message || 'Failed to delete');
                    }

                    Swal.fire('Deleted!', 'Customer has been deleted.', 'success');
                    // Redirect user back to customer list after delete
                    navigate('/customer');
                } catch (error) {
                    console.error('Delete error:', error.message);
                    Swal.fire('Error!', `Failed to delete customer. ${error.message}`, 'error');
                }
            }
        });
    };

    const documentUrl = customer?.document ? `${url}/${customer.document}` : null;
    const isPdf = customer?.document?.toLowerCase().endsWith(".pdf");

    return (
        <>
            <div className={`open-close ${isSidebarOpen ? "open" : "closed"}`}>
                <div className="ecom-customer-detail-container">
                    <div className="ecom-customer-header-detail">
                        <div className="ecom-customer-header-left-container">
                            <ArrowLeftIcon onClick={() => navigate('/customer')} /> <h2 className="ecom-customer-name-highlight">{customer.customerName}</h2>
                        </div>
                        <div className="ecom-customer-header-right-container">
                            <PenIcon onClick={() => handleEdit(customer)} />
                            <Trash2Icon onClick={() => handleDelete(customer.id)} />
                        </div>
                    </div>

                    {/* Personal Details */}
                    <section className="ecom-customer-section-box ecom-customer-details">
                        <h4><User size={16} style={{ marginRight: '8px' }} /> Personal Details</h4>
                        <div className="ecom-customer-detail-grid">
                            <div><label>Customer Type:</label><span>{customer.customerType}</span></div>
                            <div><label>Business Name:</label><span>{customer.companyName}</span></div>
                            <div><label>Display Name:</label><span>{customer.displayName}</span></div>
                            <div><label>Currency:</label><span>{customer.currency}</span></div>
                            <div><label>Email:</label><span>{customer.email}</span></div>
                            <div><label>Phone Number:</label><span>{customer.phone1}</span></div>
                            <div><label>Alternative Number:</label><span>{customer.phone2 || "—"}</span></div>
                        </div>
                    </section>

                    {/* Other Details */}
                    <section className="ecom-customer-section-box ecom-customer-details">
                        <h4><Info size={16} style={{ marginRight: '8px' }} /> Other Details</h4>
                        <div className="ecom-customer-detail-grid">
                            <div><label>GST Number:</label><span>{customer.gstNumber || "—"}</span></div>
                            <div><label>Place of Supply:</label><span>{customer.placeOfSupply || "—"}</span></div>
                            <div><label>PAN:</label><span>{customer.pan || "—"}</span></div>
                            <div><label>Website:</label><span>{customer.website_url || "—"}</span></div>
                            <div><label>Department:</label><span>{customer.department || "—"}</span></div>
                            <div><label>Designation:</label><span>{customer.designation || "—"}</span></div>
                            <div><label>Twitter:</label><span>{customer.twitter || "—"}</span></div>
                            <div><label>Skype:</label><span>{customer.skype || "—"}</span></div>
                            <div><label>Facebook:</label><span>{customer.facebook || "—"}</span></div>
                            <div><label>Sales Person/ID:</label><span>{customer.sales_person || customer.sales_id || "—"}</span></div>
                        </div>
                    </section>

                    {/* Address Details */}
                    <section className="ecom-customer-section-box ecom-customer-details">
                        <h4><MapPin size={16} style={{ marginRight: '8px' }} /> Address Details</h4>
                        <div className="ecom-customer-sentence-view">

                            {/* Billing Address Sentence */}
                            <div>
                                <strong>Billing Address:</strong>{" "}
                                {billingAddress?.attention && `${billingAddress.attention}, `}
                                {billingAddress?.address && `${billingAddress.address}, `}
                                {billingAddress?.city && `${billingAddress.city}, `}
                                {billingAddress?.state && `${billingAddress.state}, `}
                                {billingAddress?.country && `${billingAddress.country}, `}
                                {billingAddress?.pincode && `${billingAddress.pincode}, `}
                                {billingAddress?.number && `${billingAddress.number}`}
                            </div>

                            {/* Shipping Address Sentence */}
                            <div style={{ marginTop: "12px" }}>
                                <strong>Shipping Address:</strong>{" "}
                                {shippingAddress?.attention && `${shippingAddress.attention}, `}
                                {shippingAddress?.address && `${shippingAddress.address}, `}
                                {shippingAddress?.city && `${shippingAddress.city}, `}
                                {shippingAddress?.state && `${shippingAddress.state}, `}
                                {shippingAddress?.country && `${shippingAddress.country}, `}
                                {shippingAddress?.pincode && `${shippingAddress.pincode}, `}
                                {shippingAddress?.number && `${shippingAddress.number}`}
                            </div>

                        </div>
                    </section>


                    {/* Contact Details */}
                    <section className="ecom-customer-section-box ecom-customer-details">
                        <h4><Phone size={16} style={{ marginRight: '8px' }} /> Contact Details</h4>
                        <div className="ecom-customer-detail-grid">
                            <div><label>First Name:</label><span>{firstContact.firstName || "—"}</span></div>
                            <div><label>Last Name:</label><span>{firstContact.lastName || "—"}</span></div>
                            <div><label>Email:</label><span>{firstContact.email || "—"}</span></div>
                            <div><label>Contact No.:</label><span>{firstContact.contactNo || "—"}</span></div>
                            <div><label>Alternative No.:</label><span>{firstContact.anotherContactNo || "—"}</span></div>
                        </div>
                    </section>

                    {/* Remarks */}
                    <section className="ecom-customer-section-box ecom-customer-details">
                        <h4><StickyNote size={16} style={{ marginRight: '8px' }} /> Remarks</h4>
                        <p className="remarks">{customer.remarks || "—"}</p>
                    </section>
                    {
                        documentUrl && <section className="ecom-customer-section-box ecom-customer-details">
                            <h4><FileText size={16} style={{ marginRight: '8px' }} /> Document</h4>
                            {/* <p className="remarks">{customer.remarks || "—"}</p> */}

                            {documentUrl && (
                                <a href={documentUrl} target="_blank" rel="noopener noreferrer">
                                    {isPdf ? (
                                        <iframe
                                            src={documentUrl}
                                            type="application/pdf"
                                            width="100px"
                                            height="140px"
                                            style={{ marginTop: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                                            title="PDF Preview"
                                        />

                                    ) : (
                                        <img
                                            src={documentUrl}
                                            alt="Customer Document"
                                            style={{ width: '100px', marginTop: '10px', borderRadius: '4px' }}
                                        />
                                    )}
                                </a>
                            )}
                        </section>
                    }

                    <section className="ecom-customer-section-box ecom-customer-details">
                        <h4><Phone size={16} style={{ marginRight: '8px' }} />Other Contact Details</h4>
                        {customer?.contact.length > 0 ? (
                            customer?.contact.map((contact, index) => (
                                <div key={index} className="ecom-customer-detail-grid" style={{ marginBottom: '1.5rem' }}>
                                    {contact.firstName && (
                                        <div><label>First Name:</label> <span>{contact.firstName}</span></div>
                                    )}
                                    {contact.lastName && (
                                        <div><label>Last Name:</label> <span>{contact.lastName}</span></div>
                                    )}
                                    {contact.email && (
                                        <div><label>Email:</label> <span>{contact.email}</span></div>
                                    )}
                                    {contact.contactNo && (
                                        <div><label>Contact No.:</label> <span>{contact.contactNo}</span></div>
                                    )}
                                    {contact.anotherContactNo && (
                                        <div><label>Alternative No.:</label> <span>{contact.anotherContactNo}</span></div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <p>No contact details available.</p>
                        )}

                        {/* <div className="ecom-customer-detail-grid">
                            <div><label>First Name:</label><span>{firstContact.firstName || "—"}</span></div>
                            <div><label>Last Name:</label><span>{firstContact.lastName || "—"}</span></div>
                            <div><label>Email:</label><span>{firstContact.email || "—"}</span></div>
                            <div><label>Contact No.:</label><span>{firstContact.contactNo || "—"}</span></div>
                            <div><label>Alternative No.:</label><span>{firstContact.anotherContactNo || "—"}</span></div>
                        </div> */}
                    </section>

                </div>
            </div>
        </>
    );
};

export default CustomerDetail;
